import React from 'react';

import KeyStatTileSkeleton from '../../KeyStatTileSkeleton';
import { formatMoney } from '../budget_overview/budgetOverviewFormat';
import BudgetOverviewInfoTip from '../budget_overview/BudgetOverviewInfoTip';
import { fxBasisText } from '../ShowInSelect';

import 'react-loading-skeleton/dist/skeleton.css';

const EM_DASH = '—';

export const kpiDefinitions = () => ({
    revenue: { label: $t('Revenue'), text: $t('Budgeted revenue in the selected period.') },
    expenditure: { label: $t('Expenditure'), text: $t('Budgeted expenditure in the selected period.') },
    net: { label: $t('Net'), text: $t('Revenue less expenditure.') },
});

// Intentionally does not reuse CommonComponents/KeyStatTile: that tile models a
// ±% variance with an up/down triangle, whereas these KPI tiles need a free-text
// context line ("across N projects" / "X% net margin") and a sign-based Net colour.
// The shared KeyStatTileSkeleton is still reused for the loading state.
function KpiTile({ definition, value, valueClassName, context, currency = null }) {
    return (
        <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-grey">
            <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">
                {definition.label}
                <BudgetOverviewInfoTip label={definition.label} text={definition.text} />
            </div>
            <div className={`tw-text-2.5xl tw-font-semibold ${valueClassName || 'tw-text-charcoal'}`}>
                {value}
                {currency && <span className="tw-ms-1 tw-text-xs tw-font-medium tw-text-charcoal-50">{currency}</span>}
            </div>
            {context && <div className="tw-text-xs tw-text-charcoal-50 tw-mt-1">{context}</div>}
        </div>
    );
}

// Net margin line: a percentage when revenue is non-zero, a dash otherwise
// (revenue of 0 makes the ratio undefined).
function marginContext(kpis) {
    return typeof kpis.margin === 'number' ? $t('{{margin}}% net margin', { margin: kpis.margin.toFixed(1) }) : $t('— net margin');
}

export default function KpiTiles({ loading, kpis, selectedCount, committedActuals, fx = null }) {
    const currency = fx?.show_in || null;

    if (loading) {
        return (
            <div className="tw-grid tw-grid-cols-3 tw-gap-x-5 tw-gap-y-3 tw-mb-4">
                {[0, 1, 2].map((i) => (
                    <KeyStatTileSkeleton key={i} />
                ))}
            </div>
        );
    }

    // With no projects selected no data is fetched (kpis is null) — render
    // em-dash tiles rather than a perpetual skeleton.
    const hasData = selectedCount > 0 && !!kpis;

    const revenueValue = hasData ? formatMoney(kpis.revenue) : EM_DASH;
    const expenditureValue = hasData ? formatMoney(kpis.expenditure) : EM_DASH;
    const netValue = hasData ? formatMoney(kpis.net) : EM_DASH;

    const count = hasData ? kpis.project_count : 0;
    const acrossContext = hasData ? (count === 1 ? $t('across 1 project') : $t('across {{count}} projects', { count })) : null;
    const expenditureContext =
        hasData && committedActuals && kpis.committed !== undefined
            ? $t('{{committed}} committed, {{actuals}} actual to date', {
                  committed: formatMoney(kpis.committed),
                  actuals: formatMoney(kpis.actuals),
              })
            : acrossContext;
    const definitions = kpiDefinitions();
    const netColour = hasData ? (kpis.net >= 0 ? 'tw-text-green' : 'tw-text-red') : 'tw-text-charcoal';

    return (
        <div className={`tw-grid ${fx && hasData ? 'tw-grid-cols-4' : 'tw-grid-cols-3'} tw-gap-x-5 tw-gap-y-3 tw-mb-4`}>
            <KpiTile definition={definitions.revenue} value={revenueValue} context={acrossContext} currency={hasData ? currency : null} />
            <KpiTile definition={definitions.expenditure} value={expenditureValue} context={expenditureContext} currency={hasData ? currency : null} />
            <KpiTile
                definition={definitions.net}
                value={netValue}
                valueClassName={netColour}
                context={hasData ? marginContext(kpis) : null}
                currency={hasData ? currency : null}
            />
            {fx && hasData && (
                <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-grey">
                    <div className="tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{$t('Basis')}</div>
                    <div className="tw-text-sm tw-text-charcoal">{fxBasisText(fx)}</div>
                </div>
            )}
        </div>
    );
}
